import React, { useState, useEffect } from 'react';
import axios from 'axios';


const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '');

function WIMPayment() {

  // Data states
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedViolation, setSelectedViolation] = useState(null);

  // Payment states
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [cardId, setCardId] = useState('');
  const [balance, setBalance] = useState(null);

  // UI states
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('unpaid');

  // Load violations on mount and when filter changes
  // Load violations from backend
  const loadViolations = React.useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        setMessage({ type: 'error', text: 'Please login first' });
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${API_URL}/api/wim/violations?status=${filterStatus}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setViolations(response.data || []);
      setMessage({ type: '', text: '' });
    } catch (error) {
      console.error('Load violations error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load violations'
      });
      setViolations([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  // Load violations on mount and when filter changes
  useEffect(() => {
    loadViolations();
  }, [loadViolations]);

  // Check balance
  const checkBalance = React.useCallback(async (card, deviceType) => {
    if (!card) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/manual-payment/check-balance`,
        { cardId: card, deviceType },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setBalance(response.data.balance);
      }
    } catch (error) {
      console.error('Balance check error:', error);
      setBalance(null);
    }
  }, []);

  // Check balance when card ID changes
  useEffect(() => {
    if (cardId && cardId.length >= 8) {
      checkBalance(cardId, paymentMethod === 'card' ? 'mifare' : 'obu');
    } else {
      setBalance(null);
    }
  }, [cardId, paymentMethod, checkBalance]);

  // Open payment modal
  const openPaymentModal = (violation) => {
    setSelectedViolation(violation);
    setShowPaymentModal(true);
    setCardId('');
    setBalance(null);
    setMessage({ type: '', text: '' });
  };

  // Close payment modal
  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedViolation(null);
    setCardId('');
    setBalance(null);
    setMessage({ type: '', text: '' });
  };

  // Process payment
  const processPayment = async (e) => {
    e.preventDefault();

    if (!selectedViolation) return;

    if (!cardId) {
      setMessage({ type: 'error', text: 'Please enter Card ID or Device ID' });
      return;
    }

    const amount = parseFloat(selectedViolation.fine_amount || 0);
    const currentBalance = parseFloat(balance);

    if (balance !== null && currentBalance < amount) {
      setMessage({ type: 'error', text: 'Insufficient balance' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const payload = {
        method: paymentMethod === 'card' ? 'mifare' : 'obu',
        card_uid: paymentMethod === 'card' ? cardId : undefined,
        device_id: paymentMethod === 'obu' ? cardId : undefined
      };

      const response = await axios.post(
        `${API_URL}/api/wim/violations/${selectedViolation.id}/pay`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });

        // Generate and open PDF receipt
        try {
          const violationId = response.data.violationId || selectedViolation.id;
          console.log('📝 Generating PDF for violation:', violationId);

          const pdfResponse = await axios.get(
            `${API_URL}/api/wim/receipt-pdf/${violationId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob'
            }
          );

          // Create blob URL and open in new tab
          const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
          const blobUrl = window.URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');

          console.log('✅ PDF receipt opened successfully');

          // Clean up blob URL after a delay
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
        } catch (pdfError) {
          console.error('❌ PDF generation error:', pdfError);
          console.error('PDF error details:', pdfError.response?.data);
          // Don't fail the whole payment if PDF fails
          setMessage({ type: 'success', text: response.data.message + ' (PDF generation failed)' });
        }

        // Wait 2 seconds to show success message, then close and refresh
        setTimeout(async () => {
          closePaymentModal();
          // Immediately reload violations list
          await loadViolations();
          console.log('✅ Violations list refreshed');
        }, 2000);
      }
    } catch (error) {
      console.error('Payment error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Payment failed'
      });
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format number with commas
  const formatNumber = (num) => {
    if (num == null) return '-';
    return parseFloat(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-t-xl shadow-lg p-6 border-b-4 border-purple-500">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white flex items-center gap-3">
            <span className="text-4xl">⚖️</span>
            WIM Payment - ຊຳລະຄ່າປັບ WIM
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            Weigh-In-Motion Violation Payment System - ລະບົບຊຳລະຄ່າປັບນ້ຳໜັກເກີນ
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="bg-white dark:bg-gray-800 shadow-lg p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex gap-3">
            <button
              onClick={() => setFilterStatus('unpaid')}
              className={`px-6 py-2 rounded-lg font-semibold transition ${filterStatus === 'unpaid'
                ? 'bg-red-500 text-white shadow-lg'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300'
                }`}
            >
              <span className="mr-2">⚠️</span>
              Unpaid - ຍັງບໍ່ຈ່າຍ
            </button>
            <button
              onClick={() => setFilterStatus('paid')}
              className={`px-6 py-2 rounded-lg font-semibold transition ${filterStatus === 'paid'
                ? 'bg-green-500 text-white shadow-lg'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300'
                }`}
            >
              <span className="mr-2">✅</span>
              Paid - ຈ່າຍແລ້ວ
            </button>
            <button
              onClick={() => setFilterStatus('')}
              className={`px-6 py-2 rounded-lg font-semibold transition ${filterStatus === ''
                ? 'bg-blue-500 text-white shadow-lg'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300'
                }`}
            >
              <span className="mr-2">📋</span>
              All - ທັງໝົດ
            </button>
            <button
              onClick={loadViolations}
              className="ml-auto px-6 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-semibold transition shadow"
            >
              <span className="mr-2">🔄</span>
              Refresh
            </button>
          </div>
        </div>

        {/* Alert Messages */}
        {message.text && (
          <div className={`bg-white dark:bg-gray-800 shadow-lg p-4 border-l-4 ${message.type === 'success' ? 'border-green-500 bg-green-50 dark:bg-green-900' :
            message.type === 'error' ? 'border-red-500 bg-red-50 dark:bg-red-900' :
              'border-blue-500 bg-blue-50 dark:bg-blue-900'
            }`}>
            <p className={`text-sm ${message.type === 'success' ? 'text-green-800 dark:text-green-200' :
              message.type === 'error' ? 'text-red-800 dark:text-red-200' :
                'text-blue-800 dark:text-blue-200'
              }`}>
              {message.text}
            </p>
          </div>
        )}

        {/* Violations Table */}
        <div className="bg-white dark:bg-gray-800 rounded-b-xl shadow-lg overflow-hidden">
          {loading && violations.length === 0 ? (
            <div className="flex items-center justify-center p-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              <span className="ml-4 text-gray-600 dark:text-gray-300">Loading violations...</span>
            </div>
          ) : violations.length === 0 ? (
            <div className="text-center p-12">
              <span className="text-6xl">📭</span>
              <p className="text-gray-500 dark:text-gray-400 mt-4 text-lg">
                No violations found - ບໍ່ມີລາຍການປັບ
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900 dark:to-indigo-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      Event Time<br />ເວລາ
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      License Plate<br />ປ້າຍທະບຽນ
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      Gross Weight<br />ນ້ຳໜັກລວມ (kg)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      Legal Weight<br />ນ້ຳໜັກຕາມກົດໝາຍ (kg)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      Overweight<br />ນ້ຳໜັກເກີນ (kg)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      Fine Amount<br />ຄ່າປັບ (₭)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      Status<br />ສະຖານະ
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      Action<br />ດຳເນີນການ
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {violations.map((violation, idx) => (
                    <tr
                      key={violation.id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-750'
                        }`}
                    >
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {formatDate(violation.event_time)}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white">
                        {violation.license_plate || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 text-right">
                        {formatNumber(violation.gross_weight_kg)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 text-right">
                        {formatNumber(violation.legal_weight_kg)}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-red-600 dark:text-red-400 text-right">
                        {formatNumber(violation.overweight_kg || violation.event_overweight)}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-purple-600 dark:text-purple-400 text-right">
                        {formatNumber(violation.fine_amount)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {violation.status === 'paid' ? (
                          <span className="px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full text-xs font-bold">
                            ✅ Paid
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full text-xs font-bold">
                            ⚠️ Unpaid
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {violation.status === 'unpaid' && (
                          <button
                            onClick={() => openPaymentModal(violation)}
                            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white text-xs font-bold rounded-lg shadow transition transform hover:scale-105"
                          >
                            💳 Pay Now
                          </button>
                        )}
                        {violation.status === 'paid' && (
                          <span className="text-gray-400 text-xs">
                            {formatDate(violation.paid_at)}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedViolation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">

            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 rounded-t-xl">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <span className="text-3xl">💳</span>
                Process Payment - ຊຳລະຄ່າປັບ
              </h2>
            </div>

            {/* Modal Body */}
            <div className="p-6">

              {/* Alert Messages */}
              {message.text && (
                <div className={`mb-4 p-4 rounded-lg border-l-4 ${message.type === 'success' ? 'bg-green-50 border-green-500 text-green-800 dark:bg-green-900 dark:text-green-200' :
                  message.type === 'error' ? 'bg-red-50 border-red-500 text-red-800 dark:bg-red-900 dark:text-red-200' :
                    'bg-blue-50 border-blue-500 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                  }`}>
                  {message.text}
                </div>
              )}

              {/* Vehicle Information */}
              {selectedViolation.vehicle_id && (
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900 dark:to-cyan-900 rounded-lg p-6 mb-4 border-2 border-blue-200 dark:border-blue-700">
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                    <span className="text-2xl">🚗</span>
                    Vehicle Information - ຂໍ້ມູນລົດ
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">License Plate:</span>
                      <p className="font-bold text-gray-900 dark:text-white text-lg">{selectedViolation.license_plate}</p>
                    </div>
                    {selectedViolation.vehicle_type_lao && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Vehicle Type:</span>
                        <p className="font-bold text-gray-900 dark:text-white">{selectedViolation.vehicle_type_lao}</p>
                      </div>
                    )}
                    {selectedViolation.make && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Make/Model:</span>
                        <p className="font-bold text-gray-900 dark:text-white">{selectedViolation.make} {selectedViolation.model || ''}</p>
                      </div>
                    )}
                    {selectedViolation.owner_name && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Owner:</span>
                        <p className="font-bold text-gray-900 dark:text-white">{selectedViolation.owner_name}</p>
                      </div>
                    )}
                    {selectedViolation.owner_phone && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Phone:</span>
                        <p className="font-bold text-gray-900 dark:text-white">{selectedViolation.owner_phone}</p>
                      </div>
                    )}
                    {selectedViolation.vehicle_legal_weight && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Legal Weight:</span>
                        <p className="font-bold text-gray-900 dark:text-white">{formatNumber(selectedViolation.vehicle_legal_weight)} kg</p>
                      </div>
                    )}
                  </div>

                  {/* Device Balances */}
                  {(selectedViolation.mifare_card || selectedViolation.obu_device) && (
                    <div className="mt-4 pt-4 border-t-2 border-blue-300 dark:border-blue-700">
                      <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">ຄືົນຸປກອນຊຳລະ:</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {selectedViolation.mifare_card && (
                          <div className="bg-blue-100 dark:bg-blue-800 p-3 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xl">💳</span>
                              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">MIFARE Card</span>
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 font-mono">{selectedViolation.mifare_card}</p>
                            <p className={`text-lg font-bold mt-1 ${parseFloat(selectedViolation.mifare_balance || 0) >= parseFloat(selectedViolation.fine_amount) ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                              {formatNumber(selectedViolation.mifare_balance || 0)} ₭
                            </p>
                          </div>
                        )}
                        {selectedViolation.obu_device && (
                          <div className="bg-purple-100 dark:bg-purple-800 p-3 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xl">📡</span>
                              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">OBU Device</span>
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 font-mono">{selectedViolation.obu_device}</p>
                            <p className={`text-lg font-bold mt-1 ${parseFloat(selectedViolation.obu_balance || 0) >= parseFloat(selectedViolation.fine_amount) ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                              {formatNumber(selectedViolation.obu_balance || 0)} ₭
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Violation Details */}
              <div className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900 dark:to-orange-900 rounded-lg p-6 mb-6 border-2 border-red-200 dark:border-red-700">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4">
                  Violation Details - ລາຍລະອຽດການລະເມີດ
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Event Time:</span>
                    <p className="font-bold text-gray-900 dark:text-white">{formatDate(selectedViolation.event_time)}</p>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Axle Count:</span>
                    <p className="font-bold text-gray-900 dark:text-white">{selectedViolation.axle_count || '-'} ເພົາ</p>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Gross Weight:</span>
                    <p className="font-bold text-gray-900 dark:text-white">{formatNumber(selectedViolation.gross_weight_kg)} kg</p>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Legal Weight:</span>
                    <p className="font-bold text-gray-900 dark:text-white">{formatNumber(selectedViolation.legal_weight_kg)} kg</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600 dark:text-gray-400">Overweight:</span>
                    <p className="font-bold text-red-600 dark:text-red-400 text-xl">
                      {formatNumber(selectedViolation.overweight_kg || selectedViolation.event_overweight)} kg
                    </p>
                  </div>
                  <div className="col-span-2 pt-4 border-t-2 border-red-300 dark:border-red-700">
                    <span className="text-gray-700 dark:text-gray-300 font-semibold">Total Fine Amount:</span>
                    <p className="font-bold text-purple-600 dark:text-purple-400 text-3xl">
                      {formatNumber(selectedViolation.fine_amount)} ₭
                    </p>
                  </div>
                  {selectedViolation.tier_name && (
                    <div className="col-span-2">
                      <span className="text-gray-600 dark:text-gray-400">Fine Tier:</span>
                      <p className="font-bold text-gray-900 dark:text-white">{selectedViolation.tier_name}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Form */}
              <form onSubmit={processPayment}>

                {/* Payment Method */}
                <div className="mb-6">
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                    Payment Method - ວິທີຊຳລະເງິນ <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${paymentMethod === 'card'
                      ? 'bg-blue-100 border-blue-500 dark:bg-blue-900 dark:border-blue-400'
                      : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600'
                      }`}>
                      <input
                        type="radio"
                        value="card"
                        checked={paymentMethod === 'card'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="w-5 h-5"
                      />
                      <span className="text-2xl">💳</span>
                      <span className="font-semibold text-gray-800 dark:text-white">MIFARE Card</span>
                    </label>

                    <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${paymentMethod === 'obu'
                      ? 'bg-purple-100 border-purple-500 dark:bg-purple-900 dark:border-purple-400'
                      : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600'
                      }`}>
                      <input
                        type="radio"
                        value="obu"
                        checked={paymentMethod === 'obu'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="w-5 h-5"
                      />
                      <span className="text-2xl">📡</span>
                      <span className="font-semibold text-gray-800 dark:text-white">OBU Device</span>
                    </label>
                  </div>
                </div>

                {/* Card/Device ID */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {paymentMethod === 'card' ? '💳 Card UID' : '📡 Device ID'} <span className="text-red-500">*</span>
                  </label>
                  {/* Show linked device info if available */}
                  {paymentMethod === 'card' && selectedViolation.mifare_card && (
                    <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900 rounded border border-blue-200 dark:border-blue-700">
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        ℹ️ Linked Card: <span className="font-mono font-bold">{selectedViolation.mifare_card}</span>
                        <button
                          type="button"
                          onClick={() => setCardId(selectedViolation.mifare_card)}
                          className="ml-2 text-blue-600 hover:text-blue-800 underline text-xs"
                        >
                          Use This
                        </button>
                      </p>
                    </div>
                  )}
                  {paymentMethod === 'obu' && selectedViolation.obu_device && (
                    <div className="mb-2 p-2 bg-purple-50 dark:bg-purple-900 rounded border border-purple-200 dark:border-purple-700">
                      <p className="text-xs text-purple-700 dark:text-purple-300">
                        ℹ️ Linked Device: <span className="font-mono font-bold">{selectedViolation.obu_device}</span>
                        <button
                          type="button"
                          onClick={() => setCardId(selectedViolation.obu_device)}
                          className="ml-2 text-purple-600 hover:text-purple-800 underline text-xs"
                        >
                          Use This
                        </button>
                      </p>
                    </div>
                  )}
                  <input
                    type="text"
                    value={cardId}
                    onChange={(e) => setCardId(e.target.value)}
                    onKeyDown={(e) => {
                      // Prevent form submission on Enter key
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    className="w-full p-4 text-lg border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 font-mono"
                    placeholder={paymentMethod === 'card' ? 'Enter Card UID' : 'Enter OBU Device ID'}
                    required
                  />
                  {balance !== null && (
                    <div className={`mt-3 p-4 rounded-lg border-2 ${parseFloat(balance) >= parseFloat(selectedViolation.fine_amount)
                      ? 'bg-green-50 border-green-500 dark:bg-green-900 dark:border-green-500'
                      : 'bg-red-50 border-red-500 dark:bg-red-900 dark:border-red-500'
                      }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Current Balance:
                          </p>
                          <p className={`text-2xl font-bold ${parseFloat(balance) >= parseFloat(selectedViolation.fine_amount)
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                            }`}>
                            {formatNumber(balance)} ₭
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Required:
                          </p>
                          <p className="text-xl font-bold text-gray-900 dark:text-white">
                            {formatNumber(selectedViolation.fine_amount)} ₭
                          </p>
                        </div>
                      </div>
                      {parseFloat(balance) < parseFloat(selectedViolation.fine_amount) && (
                        <p className="mt-2 text-sm font-bold text-red-700 dark:text-red-300">
                          ⚠️ Insufficient balance - ຍອດເງິນບໍ່ພຽງພໍ
                        </p>
                      )}
                      {parseFloat(balance) >= parseFloat(selectedViolation.fine_amount) && (
                        <p className="mt-2 text-sm font-bold text-green-700 dark:text-green-300">
                          ✅ Balance is sufficient - ຍອດເງິນພຽງພໍ
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={loading || (balance !== null && parseFloat(balance) < parseFloat(selectedViolation.fine_amount))}
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-lg"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <span className="text-2xl">✅</span>
                        Confirm Payment
                      </span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={closePaymentModal}
                    disabled={loading}
                    className="px-6 py-4 bg-gray-500 hover:bg-gray-600 text-white font-bold rounded-xl shadow transition disabled:opacity-50"
                  >
                    ❌ Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WIMPayment;
